import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";

import { ErrorText } from "~/common/components";
import Input from "~/common/components/ui/Input";
import Select from "~/common/components/ui/select/Select";
import { EmergencyContactsData, formatPhoneNumberField } from "~/common/utils";
import { ConsultationQuestionsLayout } from "~/patients/components";
import { RELATIONSHIPS, relationshipsOptions } from "~/patients/constants";
import {
  emergencyContactFormSchema,
  EmergencyContactFormValues,
  EmergencyContactSchemaType,
} from "~/patients/utils";

export interface EmergencyContactsFormProps {
  question: EmergencyContactsData;
  handleGoBack: () => void;
  onSubmit: (answer: EmergencyContactFormValues) => void;
  isSubmitting: boolean;
  disableBack: boolean;
  completedAnswers: EmergencyContactSchemaType;
}

export const EmergencyContactsForm = ({
  completedAnswers,
  disableBack,
  handleGoBack,
  isSubmitting,
  onSubmit,
  question,
}: EmergencyContactsFormProps) => {
  const { title } = question;
  const defaultValues = completedAnswers;

  const {
    handleSubmit,
    watch,
    control,
    register,
    formState: { errors },
  } = useForm<EmergencyContactFormValues>({
    defaultValues: {
      answer: defaultValues,
    },
    resolver: zodResolver(emergencyContactFormSchema),
  });

  const isOtherRelationship =
    watch("answer.relationship") === RELATIONSHIPS.OTHER;

  const saveAnswers: SubmitHandler<EmergencyContactFormValues> = (data) =>
    onSubmit(data);

  const name = watch("answer.name");
  const lastName = watch("answer.last_name");
  const relationship = watch("answer.relationship");
  const email = watch("answer.email");
  const phoneNumber = watch("answer.phone_number");

  const isNextDisabled = [
    name,
    lastName,
    relationship,
    email,
    phoneNumber,
  ].some((field) => !field);

  return (
    <ConsultationQuestionsLayout
      handleNext={handleSubmit(saveAnswers)}
      handleBack={handleGoBack}
      disableNext={isNextDisabled}
      isSubmitting={isSubmitting}
      title={title}
      disableBack={disableBack}
    >
      <Input
        id="first_name"
        label="First name"
        placeholder="Enter name"
        {...register("answer.name")}
        error={errors?.answer?.name?.message}
      />
      <Input
        id="last_name"
        label="Last name"
        placeholder="Enter last name"
        {...register("answer.last_name")}
        error={errors?.answer?.last_name?.message}
      />

      <div className="flex flex-col gap-1">
        <div className="flex items-end gap-2">
          <Select
            label="Relationship"
            placeholder="Select relationship"
            id="answer.relationship"
            options={relationshipsOptions}
            control={control}
            error={errors.answer?.relationship?.message}
          />
          {isOtherRelationship && (
            <Input
              id="customRelationship"
              placeholder="Enter relationship"
              {...register("answer.customRelationship")}
              isError={!!errors?.answer?.customRelationship?.message}
              containerClassName="gap-0"
            />
          )}
        </div>
        <ErrorText>{errors?.answer?.customRelationship?.message}</ErrorText>
      </div>

      <Input
        id="email"
        label="Email address"
        placeholder="Enter email"
        {...register("answer.email")}
        error={errors?.answer?.email?.message}
      />

      <Input
        id="phone_number"
        label="Mobile number"
        placeholder="Enter mobile number"
        {...register("answer.phone_number")}
        {...formatPhoneNumberField<EmergencyContactFormValues>(
          `answer.phone_number`,
          register,
        )}
        error={errors?.answer?.phone_number?.message}
      />
    </ConsultationQuestionsLayout>
  );
};
